import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import moment from 'moment';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import emailMask from 'text-mask-addons/dist/emailMask';
import { get } from 'lodash';
import cn from 'classnames';
import { states } from 'utils/states';
import { unmask, numberUnmask, floatUnmask } from 'utils/unmasks';
import { formatCurrency } from 'utils/formatCurrency';
import { toTitleCase } from 'utils/toTitleCase';
import { searchAddress, verifyAddress } from 'utils/verifyAddress';
import { EMPLOYMENT_YEARS } from 'utils/appConstants';

import imgError from 'assets/images/error.svg';

import { useConfig } from '../../config/ConfigContext';
import Header from '../../components/Header';
import ContentWrapper from '../../components/ContentWrapper';
import FormRow from '../../components/Form/FormRow';
import FormColumn from '../../components/Form/FormColumn';
import Footer from '../../components/Footer';
import Popup from '../../components/Popup';
import Pagination from '../../components/Pagination';
import Input from '../../components/Form/Input';
import Select from '../../components/Form/Select';
import Validator from '../../components/Validator';
import Button from '../../components/Button';
import Progress from '../../components/Progress';
import Headline from '../../components/Headline';
import Transition from '../../components/Transition';
import Container from '../../components/Container';
import VerifyButton from '../../components/Button/VerifyButton';
import Modal from '../../components/Modal';
import ApplicationId from '../../components/ApplicationId';
import withIPAddress from '../../components/WithIPAddress';

import {
  newNextAction,
  NEW_NEXT,
} from '../../actions/navigation';

import './style.scss';
import schema from './reviewSchema';
import { consentLabel } from './constant';

const currencyMask = createNumberMask({
  prefix: '$ ',
  allowDecimal: true,
});

const Styles = {
  ul: {
    listStyle: 'none',
    backgroundColor: 'white',
    width: '97%',
    border: '1px solid #ccc',
    borderRadius: '4px',
    boxShadow: '1px 3px 6px #eee',
    maxHeight: '115px',
    overflowY: 'auto',
    zIndex: 9999,
    textOverflow: 'ellipsis',
    display: 'block',
    position: 'absolute',
    top: '51px',
    left: '8px',
  },
  li: {
    padding: '5px 8px',
    cursor: 'pointer',
    transition: '0.3s',
    lineHeight: '18px',
    borderTop: '0.5px solid #ddd',
    marginBottom: '5px',
  },
  loader: {
    fontSize: '12px',
    position: 'absolute',
    top: '14px',
    right: '62px',
    border: '2px solid #f3f3f3',
    borderTop: '16px solid #3498db',
    borderRadius: '50%',
    width: '20px',
    height: '20px',
    borderWidth: '2px',
    animation: 'spin .7s infinite linear',
    webkitAnimation: 'spin .7s infinite linear',
  },
  wrapper: {
    position: 'relative',
  },
};

const Review = (props) => {
  const config = useConfig();
  const doneTypingInterval = 1000;
  const [isVerified, setIsVerified] = useState(false);
  const [isAddressLoading, setIsAddressLoading] = useState(false);
  const [isAddressPopupVisible, setIsAddressPopupVisible] = useState(false);
  const [popupType, setPopupType] = useState(null);
  const [isProgressVisible, setIsProgressVisible] = useState(false);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [popoverContent, setPopoverContent] = useState('');
  const [isContentPopupVisible, setIsContentPopupVisible] = useState(false);
  const [isTermsPopupVisible, setIsTermsPopupVisible] = useState(false);
  const [uploadingPercentage, setUploadingPercentage] = useState(null);
  const [prevAddresses, setPrevAddresses] = useState({});
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const { navigate, validator: { values, errors }, params, searchParams } = props;
  const employmentStatus = get(values, 'employmentStatus');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSuggestionLoading, setIsSuggestionLoading] = useState(false);
  const isEmploymentInfoRequired = !(employmentStatus === 'retired' || employmentStatus === 'unemployed' || employmentStatus === 'student');
  const dropdownRef = useRef(null);
  const fieldRef = useRef(null);
  const workflowType = get(params, 'workflowType');
  const key = searchParams.get('key');
  const test = searchParams.get('test');

  useEffect(() => {
    if (!isEmploymentInfoRequired) {
      const formData = {
        ...values,
        employmentYears: '',
      };
      props.validator.setValues(formData);
    }
  }, [isEmploymentInfoRequired]);

  const verifySmartyStreet = useCallback(({ address, city, state, zipcode }, initialFormData) => {
    const { validator: { setValues } } = props;

    setIsAddressLoading(true);

    verifyAddress({ zipcode, address, city, state }).then((response) => {
      if (response) {
        if (get(response, 'metadata.recordType') === 'P') {
          setIsAddressPopupVisible(true);
          setIsAddressLoading(false);
          setIsVerified(false);
          setPopupType(3);
          setIsFirstLoad(false);
        } else {
          const addressData = {
            ...(initialFormData || values),
            address: response.deliveryLine1,
            city: get(response, 'components.cityName'),
            state: get(response, 'components.state'),
            zip: `${response.deliveryPointBarcode}`.slice(0, 5),
          };
          const hasOptional = `${get(response, 'analysis.footnotes', '')}`.indexOf('H#') !== -1;
          setIsVerified(true);
          setIsAddressLoading(false);
          setPrevAddresses({ address, city, state, zip: zipcode });
          setPopupType(hasOptional ? 5 : null);
          setIsAddressPopupVisible(hasOptional);
          setIsFirstLoad(false);
          setValues(addressData);
        }
      } else {
        setIsAddressPopupVisible(true);
        setIsAddressLoading(false);
        setIsVerified(false);
        setPopupType(2);
        setIsFirstLoad(false);
      }
    }).catch(() => {
      setIsAddressLoading(false);
      setPopupType(1);
      setIsVerified(false);
      setIsFirstLoad(false);
    });
  }, [props.validator.values]);

  useEffect(() => {
    const { validator: { setValues }, checkinData, ipAddress, testSubjectData } = props;
    const sourceData = get(checkinData, ['data', 'data']);

    let setTestSubjectValues = {};
    if (testSubjectData && testSubjectData.reviewDetails && testSubjectData.reviewDetails.value) {
      const { value } = testSubjectData.reviewDetails;
      setTestSubjectValues = {
        ...value,
        agreeTerms: true,
      };
    }

    if (sourceData === undefined) {
      if (test) {
        navigate(`${workflowType ? `/${workflowType}` : ''}/?key=${key}&test=${test}`);
      } else {
        navigate(`${workflowType ? `/${workflowType}` : ''}/?key=${key}`);
      }
    } else {
      const initialFormData = {
        ...sourceData,
        annualGrossIncome: sourceData.annualGrossIncome ? `$ ${formatCurrency(floatUnmask(`${sourceData.annualGrossIncome}`), 2)}` : '',
        sourceIpAddress: ipAddress,
        dateOfBirth: sourceData.dateOfBirth ? moment(sourceData.dateOfBirth).format('MM/DD/YYYY') : null,
        ...setTestSubjectValues,
        workflowType,
      };

      setValues(initialFormData);
      verifySmartyStreet({
        address: initialFormData.address,
        city: initialFormData.city,
        state: initialFormData.state,
        zipcode: initialFormData.zip,
      }, initialFormData);
    }
  }, []);

  useEffect(() => {
    const { fetch, checkinData } = props;

    if (fetch.isDone[`${NEW_NEXT}`] && !fetch.isRunning[`${NEW_NEXT}`] && !isFirstLoad) {
      const routeUrl = checkinData.data && checkinData.data.url.replace(config.hostUrl, '');
      navigate(`${routeUrl}`);
    }
  }, [props.checkinData]);

  const handleCheckboxChange = (value, name) => {
    const { validator: { onChangeHandler } } = props;
    onChangeHandler(name, value);
  };

  const initializeAddressState = () => {
    setIsVerified(false);
  };

  const handleInputChange = (event) => {
    const { validator: { onChangeHandler } } = props;
    switch (event.target.name) {
      case 'address':
        initializeAddressState();
        onChangeHandler(event.target.name, (event.target.value).replace(/[^a-zA-Z0-9-.,#/;:'&%@ ]/g, ''));
        break;
      case 'city':
        initializeAddressState();
        onChangeHandler(event.target.name, (event.target.value).replace(/[^a-zA-Z ']/g, ''));
        break;
      case 'zip':
      case 'state':
        initializeAddressState();
        onChangeHandler(event.target.name, event.target.value);
        break;
      default:
        onChangeHandler(event.target.name, event.target.value);
    }
  };

  const handleVerifyAddressClick = (e) => {
    e && e.preventDefault();

    if (!get(values, 'address')) {
      setIsAddressPopupVisible(true);
      setPopupType(1);
    } else {
      verifySmartyStreet({
        address: get(values, 'address'),
        city: get(values, 'city'),
        state: get(values, 'state'),
        zipcode: get(values, 'zip'),
      });
    }
  };

  const onSuggestionClick = (suggestion) => {
    const { validator: { setValues } } = props;

    const updatedValues = { ...values };
    updatedValues.address = `${suggestion.streetLine} ${suggestion.secondary || ''}`.trim();
    updatedValues.city = suggestion.city;
    updatedValues.state = suggestion.state;
    updatedValues.zip = suggestion.zipcode;
    setValues(updatedValues);
    setShowSuggestions(false);
    setSuggestions([]);
  };
  let typingTimer;

  const doneTyping = async () => {
    console.log('values', values);
    try {
      const results = await searchAddress({
        address: values.address,
        city: values.city,
        state: values.state,
        zip: values.zip,
      });
      setSuggestions(results);
      if (results.length > 0) {
        setShowSuggestions(true);
      }
    } catch {
      setSuggestions([]);
      setShowSuggestions(false);
    }
    setIsSuggestionLoading(false);
  };

  const onKeyUpHandler = () => {
    clearTimeout(typingTimer);
    setIsSuggestionLoading(true);
    typingTimer = setTimeout(doneTyping, doneTypingInterval);
  };

  const onKeyDownHandler = () => {
    clearTimeout(typingTimer);
  };

  const handleSubmitForm = (e) => {
    e.preventDefault();
    const { dispatch, validator: { validate } } = props;

    if (validate(schema).isValid) {
      const formData = {
        ...values,
        annualGrossIncome: unmask(values.annualGrossIncome.toString()),
        [values.hasSsn ? 'ssnLast4Digits' : 'ssn']: numberUnmask(values.ssn),
        dateOfBirth: values.dateOfBirth,
        consentElectronicCommunication: true,
        consentToTermsAndConditions: true,
      };

      if (values.hasSsn) {
        delete formData.ssn;
      }

      setIsProgressVisible(true);

      const data = test
        ? {
          ...formData,
          test,
        }
        : { ...formData };
      dispatch(newNextAction({
        key,
        workflowType: workflowType || 'text2apply',
        data,
        onUploadProgress: (progressEvent) => {
          setUploadingPercentage(Math.floor((progressEvent.loaded * 100) / progressEvent.total));
        },
        fail: (err) => {
          console.log(err);
          setIsPopupVisible(true);
          setIsProgressVisible(false);
        },
      }));
    } else {
      console.log(errors);
      setIsPopupVisible(true);
      setIsProgressVisible(false);
    }
  };

  const handleEditClick = (value, e) => {
    e.preventDefault();
    if (!value) {
      handleVerifyAddressClick();
    }
    setIsEditingAddress(value);
  };

  const handleClosePopup = () => {
    setIsPopupVisible(false);
  };

  const handleAbort = (beGoBack) => {
    setIsAddressPopupVisible(false);
    setIsEditingAddress(beGoBack);
  };

  const handleBlur = (event) => {
    const { validator: { onChangeHandler } } = props;
    switch (event.target.name) {
      case 'annualGrossIncome':
        onChangeHandler(event.target.name, event.target.value ? formatCurrency(floatUnmask(event.target.value), 2) : '');
        break;
      case 'email':
        onChangeHandler(event.target.name, event.target.value.toLowerCase());
        break;
      default:
        onChangeHandler(event.target.name, toTitleCase(event.target.value));
    }
  };

  const toogleTermsPopup = () => {
    setIsTermsPopupVisible(!isTermsPopupVisible);
  };

  const handleUndo = (e) => {
    e.preventDefault();

    const { validator: { setValues } } = props;
    const addressData = {
      ...values,
      address: get(prevAddresses, 'address'),
      city: get(prevAddresses, 'city'),
      state: get(prevAddresses, 'state'),
      zip: get(prevAddresses, 'zip'),
    };

    setIsVerified(false);
    setValues(addressData);
  };

  const isAddressEmpty = (errors.address === 'The input field is required') || (errors.city === 'The input field is required') || (errors.state === 'The input field is required') || (errors.zip === 'The input field is required');
  let hasAddressError = false;
  if (errors.zip === 'Zipcode is not valid') {
    hasAddressError = true;
  }

  const togglePopover = (content) => {
    setPopoverContent(content);
    setIsContentPopupVisible(!isContentPopupVisible);
  };

  const combinedLabelText = (
    <p>
      <div onClick={() => togglePopover(consentLabel[0].content)} className="text-primary consentLabel">{consentLabel[0].title}</div>
      {' '}
      and
      {' '}
      <div onClick={() => togglePopover(consentLabel[1].content)} className="text-primary consentLabel">{consentLabel[1].title}</div>
    </p>
  );

  const handleClickOutside = (event) => {
    if (
      dropdownRef.current && !dropdownRef.current.contains(event.target)
        && fieldRef.current && !fieldRef.current.contains(event.target)
    ) {
      setShowSuggestions(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <Transition className="transition-item wrapper">
      <Header />
      <ContentWrapper>
        <ApplicationId id={get(props.checkinData, 'attributes.entityId')} inHeadline />
        <Headline
          appIdVisible
          header={(
            <>
              <span>Almost there!</span>
              {' '}
              <br />
              Review and submit
              {' '}
              <br />
              your application.
            </>
          )}
        />

        <Container>
          <FormRow>
            <FormColumn>
              <Input
                label="First Name"
                value={values.firstName || ''}
                name="firstName"
                onChange={handleInputChange}
                isRequired
                hasError={errors.firstName}
                onBlur={handleBlur}
              />
            </FormColumn>
          </FormRow>
          <FormRow>
            <FormColumn>
              <Input
                label="Last Name"
                value={values.lastName || ''}
                name="lastName"
                onChange={handleInputChange}
                isRequired
                hasError={errors.lastName}
                onBlur={handleBlur}
              />
            </FormColumn>
          </FormRow>
          <FormRow>
            <FormColumn>
              <Input
                label="Your Email"
                value={values.email || ''}
                name="email"
                isMasked={emailMask}
                onChange={handleInputChange}
                hasError={errors.email}
                isRequired
                onBlur={handleBlur}
              />
            </FormColumn>
          </FormRow>
          <FormRow>
            <FormColumn>
              <Input
                label="Mobile Phone Number"
                value={`${values.mobilePhoneNumber}` || ''}
                name="mobilePhoneNumber"
                isMasked={['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
                placeholder="(___) ___-____"
                isRequired
                type="tel"
                hasError={errors.mobilePhoneNumber}
                readOnly
              />
              { isAddressPopupVisible
                && (
                <Popup
                  handleAbort={handleAbort}
                  type={popupType}
                  data={{
                    address: get(values, 'applicant.addresses.address'),
                    city: get(values, 'applicant.addresses.city'),
                    state: get(values, 'applicant.addresses.state'),
                    zipcode: get(values, 'applicant.addresses.zipcode'),
                  }}
                />
                )}
            </FormColumn>
          </FormRow>
          <FormRow className="mb-1">
            <FormColumn>
              <p>
                I consent to receive calls and texts for any purpose, including marketing, under these&nbsp;
                <span className="underline text-primary " onClick={toogleTermsPopup}>terms</span>
                .
              </p>
            </FormColumn>
          </FormRow>
          <FormRow>
            <FormColumn>
              <Container className={`form-group active addressWrapper has-value ${(hasAddressError || isAddressEmpty) && 'addressRequired'}`}>
                <label>
                  <span>
                    Address
                    <em>
                      {
                        hasAddressError ? 'Error' : 'Required'
                      }
                    </em>
                  </span>
                  {
                    isEditingAddress
                      ? <button type="button" className="btn-sm" onClick={handleEditClick.bind(null, false)}>Save</button>
                      : <button type="button" className="btn-sm" onClick={handleEditClick.bind(null, true)}>Edit</button>
                  }
                </label>
                {
                  isEditingAddress
                    ? (
                      <div className="faux-input">
                        <FormRow className="editAddress">
                          <FormColumn style={Styles.wrapper}>
                            <Input
                              label="Street Address"
                              value={`${values.address || ''}`}
                              name="address"
                              placeholder="Address"
                              onChange={handleInputChange}
                              isRequired
                              hasError={errors.address}
                              onBlur={handleBlur}
                              onKeyUp={() => onKeyUpHandler()}
                              onKeyDown={(e) => onKeyDownHandler(e)}
                              onFocus={() => suggestions && suggestions.length > 0 && setShowSuggestions(true)}
                              ref={fieldRef}
                            />
                            {isSuggestionLoading ? <span className="loader" style={Styles.loader} /> : null}
                            {showSuggestions && suggestions && suggestions.length > 0 ? (
                              <ul style={Styles.ul} ref={dropdownRef}>
                                {suggestions.map((suggestion) => (
                                  <li style={Styles.li} onClick={() => onSuggestionClick(suggestion)}>
                                    {`${suggestion.streetLine} ${suggestion.secondary ? ` ${suggestion.secondary},` : ''} ${suggestion.city}, ${suggestion.state}, ${suggestion.zipcode}`}
                                  </li>
                                ))}
                              </ul>
                            ) : null}
                          </FormColumn>
                        </FormRow>
                        <FormRow>
                          <FormColumn>
                            <Input
                              label="City"
                              value={`${values.city || ''}`}
                              name="city"
                              placeholder="City"
                              onChange={handleInputChange}
                              isRequired
                              hasError={errors.city}
                              onBlur={handleBlur}
                            />
                          </FormColumn>
                        </FormRow>
                        <FormRow>
                          <FormColumn>
                            <Select
                              data={states}
                              label="State"
                              onChange={handleInputChange}
                              value={values.state || ''}
                              name="state"
                              isRequired
                              hasError={errors.state}
                              errorMessage={errors.state}
                            />
                          </FormColumn>
                        </FormRow>
                        <FormRow>
                          <FormColumn>
                            <Input
                              label="Zipcode"
                              value={`${values.zip || ''}`}
                              type="tel"
                              name="zip"
                              isMasked={[/\d/, /\d/, /\d/, /\d/, /\d/]}
                              placeholder="_____"
                              onChange={handleInputChange}
                              isRequired
                              hasError={errors.zip}
                            />
                          </FormColumn>
                        </FormRow>
                      </div>
                    )
                    : (
                      <div className="faux-input">
                        {values.address}
                        {' '}
                        <br />
                        {values.city}
                        ,
                        {values.state}
                        {' '}
                        {values.zip}
                      </div>
                    )
                }
              </Container>
            </FormColumn>
          </FormRow>
          <FormRow>
            <FormColumn>
              <VerifyButton
                onClick={isVerified ? handleUndo : handleVerifyAddressClick}
                isVerified={isVerified}
                isLoading={isAddressLoading}
              />
            </FormColumn>
          </FormRow>
          <FormRow>
            <FormColumn>
              <Input
                label="Annual Gross Income"
                value={values.annualGrossIncome ? `${values.annualGrossIncome}` : ''}
                name="annualGrossIncome"
                isMasked={currencyMask}
                onChange={handleInputChange}
                onBlur={handleBlur}
                type="tel"
                isRequired
                hasError={errors.annualGrossIncome}
              />
            </FormColumn>
          </FormRow>
          <FormRow>
            <FormColumn>
              <Select
                label="Employment Status"
                name="employmentStatus"
                data={[
                  { value: 'employed', title: 'Employed' },
                  { value: 'self-employed', title: 'Self Employed' },
                  { value: 'unemployed', title: 'Unemployed' },
                  { value: 'student', title: 'Student' },
                  { value: 'retired', title: 'Retired' },
                  { value: 'military', title: 'Military' },
                ]}
                value={values.employmentStatus || ''}
                onChange={handleInputChange}
                hasError={errors.employmentStatus}
                isRequired
              />
            </FormColumn>
          </FormRow>
          <FormRow>
            <FormColumn>
              <Select
                label="Years with Current Employer"
                name="employmentYears"
                data={Object.keys(EMPLOYMENT_YEARS).map((k) => ({ title: EMPLOYMENT_YEARS[k], value: k }))}
                value={values.employmentYears || ''}
                onChange={handleInputChange}
                hasError={errors.employmentYears}
                isRequired={isEmploymentInfoRequired}
                isDisabled={!isEmploymentInfoRequired}
              />
            </FormColumn>
          </FormRow>
          <FormRow>
            <FormColumn>
              {
                values.hasSsn
                  ? (
                    <Input
                      label="Last 4 Digits of Your SSN"
                      value={values.ssn || ''}
                      name="ssn"
                      tooltip="To protect your identity and for security purposes please enter the last 4 digits of your Social Security Number."
                      isMasked={[/\d/, /\d/, /\d/, /\d/]}
                      onChange={handleInputChange}
                      type="tel"
                      isRequired
                      hasError={errors.ssn}
                    />
                  )
                  : (
                    <Input
                      label="Full 9-Digit SSN"
                      value={values.ssn || ''}
                      name="ssn"
                      tooltip="We were unable to completely verify your identity. For this reason please enter your full 9-digit Social Security Number."
                      type="tel"
                      isMasked={[/\d/, /\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
                      onChange={handleInputChange}
                      isRequired
                      hasError={errors.ssn}
                    />
                  )
              }
            </FormColumn>
          </FormRow>
          <FormRow>
            <FormColumn>
              <Input
                label="Date Of Birth"
                type="tel"
                value={values.dateOfBirth ? `${values.dateOfBirth}` : ''}
                name="dateOfBirth"
                tooltip="Must be over 18 years old"
                onChange={handleInputChange}
                isRequired
                isMasked={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
                hasError={errors.dateOfBirth}
                placeholder="MM/DD/YYYY"
              />
            </FormColumn>
          </FormRow>
          <FormRow>
            <FormColumn>
              <Container className="form-group form-agree">
                <h3>Terms &amp; Conditions</h3>
                <div className="termsAndConditions">
                  <b>IMPORTANT INFORMATION ABOUT PROCEDURES FOR OPENING A NEW ACCOUNT -</b>
                  <br />
                  <p>To help the government fight the funding of terrorism and money laundering activities, Federal law requires all financial institutions to obtain, verify, and record information that identifies each person who opens an account.</p>
                  <p>What this means for you: When you open an account, we will ask for your name, address, date of birth, and other information that will allow us to identify you. We may also ask to see your driver&apos;s license or other identifying documents.</p>
                </div>
                <h3>By checking the box below I agree to the following: </h3>
                <div className="consentWrapper">
                  <label className={cn('popoverCheck', values.agreeTerms ? 'checked' : '')}>
                    <input
                      type="checkbox"
                      name="agreeTerms"
                      value={values.agreeTerms}
                      onClick={(e) => handleCheckboxChange(e.target.checked, 'agreeTerms')}
                    />
                  </label>
                  <div>
                    {combinedLabelText}
                  </div>
                </div>
                {
                  isContentPopupVisible
                  && (
                    <Modal>
                      <div className="card-header">
                        <h5>Terms & Conditions</h5>
                      </div>
                      <div className="close" onClick={() => setIsContentPopupVisible(false)} />
                      <div className="card-content popoverContent">
                        {popoverContent}
                      </div>
                    </Modal>
                  )
                }
              </Container>
            </FormColumn>
          </FormRow>
          <FormRow className="mb-1">
            <FormColumn>
              By selecting “CHECK MY RATE” and providing your information electronically on the application form, LendingUSA does a soft credit pull that will not impact your credit score.
            </FormColumn>
          </FormRow>
          <FormRow>
            <FormColumn>
              By providing your phone number and clicking “CHECK MY RATE”, you consent to receive autodialed text messages from LendingUSA. Consent is not required to obtain a loan. Message frequency varies. Message and data rates may apply. Text STOP to cancel, HELP for help. See our
              {' '}
              <a href="https://lendingusa.com/terms-of-use/" target="_blank" rel="noreferrer">Terms of Use</a>
              {' '}
              and
              {' '}
              <a href="https://lendingusa.com/privacy-policy/" target="_blank" rel="noreferrer">Privacy Policy</a>
              .
            </FormColumn>
          </FormRow>
        </Container>
      </ContentWrapper>
      <Footer>
        <FormRow>
          <FormColumn>
            <Button
              className="btn btn-primary"
              onClick={values.agreeTerms ? handleSubmitForm : undefined}
              title="CHECK MY RATE"
              isDisabled={!values.agreeTerms}
            />
          </FormColumn>
        </FormRow>
        <FormRow>
          <FormColumn>
            <Pagination activeIndex={2} />
          </FormColumn>
        </FormRow>
      </Footer>
      {
        isPopupVisible
          && (
          <Modal>
            <div className="card-content error-modal security-error">
              <img src={imgError} alt="" />
              <p>
                {(errors.state !== 'The input field is required' && errors.state)
                  ? `Currently we do not accept loan applications for residents of the state of ${states.filter((item) => item.value === values.state)[0].title}.`
                  : 'Please fix the errors.'}
              </p>
              <div onClick={handleClosePopup} className="btn btn-red">OK</div>
            </div>
          </Modal>
          )
      }

      {
        isTermsPopupVisible && (
          <Modal>
            <div className="card-header">
              <h5>Terms</h5>
            </div>
            <div className="close" onClick={toogleTermsPopup} />
            <div className="card-content popoverContent">
              <p>
                By providing the telephone number above and clicking &quot;CHECK MY RATE&quot;, you confirm you are the owner of the phone number(s), or that the owner authorized you to provide the phone number(s), or that you are the regular user of this phone number(s). You are providing express written consent via your electronic signature to receive any type of communication for any purpose, including telemarketing communications, from our
                {' '}
                <a href="https://lendingusa.com/lending-partners/" style={{ textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer">lending partners</a>
                , LendingUSA, their agents, representatives, affiliates, assignees or anyone calling on their behalf, at the telephone number(s) provided for any lawful purpose, even if that number is on a federal or state do-not-call list. The scope of this consent includes, but is not limited to, receiving live operator calls, calls and texts via an automatic telephone dialing system (auto-dialer), prerecorded and artificial voice messages, text/SMS messages or other means. These telephone calls and text messages may incur message, data and access fees from your telephone provider. Consent is not a condition to obtain a loan.
                <br />
                <br />

                You understand that you can request a paper copy of these terms or may withdraw your consent at any time by:
                <ul className="contact-ul">
                  <li>mailing your written request to LendingUSA c/o Compliance Department at 15021 Ventura Blvd. #879, Sherman Oaks, CA 91403;</li>
                  <li>
                    by e-mailing
                    {' '}
                    <a className="text-primary" href="mailto:customercare@lendingusa.com">customercare@lendingusa.com</a>
                    ; or
                  </li>
                  <li>by calling LendingUSA at (800) 994-6177.</li>
                </ul>
                <br />
                You may also reply “STOP” to opt out of receiving autodialed text messages only.
              </p>
              <div onClick={toogleTermsPopup} className="btn btn-primary">OK</div>
            </div>
          </Modal>
        )
      }
      {
        isProgressVisible && <Progress percent={uploadingPercentage} />
      }
    </Transition>
  );
};

Review.propTypes = {
  navigate: PropTypes.func.isRequired,
  validator: PropTypes.object.isRequired,
  checkinData: PropTypes.object.isRequired,
  fetch: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  ipAddress: PropTypes.string.isRequired,
  testSubjectData: PropTypes.object.isRequired,
  params: PropTypes.object.isRequired,
  searchParams: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  fetch: state.fetch,
  checkinData: state.screen.checkinData,
  testSubjectData: state.screen.testSubjectData,
});

export default compose(
  withIPAddress,
  Validator(schema),
  connect(mapStateToProps)
)(Review);
